import {
  altCreditAlgorithmStatesDates,
  getFinancialResponsibilityTier,
  policyType as policyTypes
} from '@ourbranch/lookups';
import { differenceInMonths, format } from 'date-fns';

export const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function calculateMarketTier({
  state,
  policyType,
  drivers,
  cars,
  priorInsuranceTerm,
  biLimit,
  rateControlDate
}) {
  // Bundled or not
  const isBundled = [
    policyTypes.HABundle,
    policyTypes.CABundle,
    policyTypes.HomeBundle,
    policyTypes.AutoBundle
  ].includes(policyType);
  const rateControlDateAsInt = Number(format(new Date(rateControlDate), 'yyyyMMdd'));

  // Insurance score
  const primaryDriver = drivers.find(({ isPrimary }) => isPrimary);
  const insuranceScore = getFinancialResponsibilityTier(
    primaryDriver.insuranceScore.autoTotal,
    primaryDriver.insuranceScore.homeTotal,
    altCreditAlgorithmStatesDates[state] && rateControlDateAsInt >= altCreditAlgorithmStatesDates[state],
    state
  ).split('-')[0];

  const aToJScore =
    'A'.charCodeAt(0) <= insuranceScore.charCodeAt(0) && insuranceScore.charCodeAt(0) <= 'J'.charCodeAt(0);
  const kToRScore =
    'K'.charCodeAt(0) <= insuranceScore.charCodeAt(0) && insuranceScore.charCodeAt(0) <= 'R'.charCodeAt(0);

  // Prior insurance term
  const atLeast6MonthsPriorInsurance = differenceInMonths(new Date(), new Date(priorInsuranceTerm)) >= 6;

  // BI limits
  const biCoverageArray = biLimit.split('/');
  const biLimits100and300orAbove = biCoverageArray[0] >= 100 && biCoverageArray[1] >= 300;
  const biLimits50and100orAbove = biCoverageArray[0] >= 50 && biCoverageArray[1] >= 100;

  const numCars = cars.length;
  const numDrivers = drivers.length;

  if (isBundled && atLeast6MonthsPriorInsurance && aToJScore && biLimits100and300orAbove) {
    return 5;
  }

  if (
    isBundled &&
    atLeast6MonthsPriorInsurance &&
    aToJScore &&
    (biLimits50and100orAbove || numCars > 1 || numDrivers > 1)
  ) {
    return 5;
  }

  if (isBundled && atLeast6MonthsPriorInsurance && kToRScore && biLimits100and300orAbove) {
    return 4;
  }

  if (
    policyType === policyTypes.Auto &&
    atLeast6MonthsPriorInsurance &&
    aToJScore &&
    (biLimits100and300orAbove || (numCars > 1 && numDrivers > 1))
  ) {
    return 4;
  }

  return 1;
}

export const sanitizeObject = (obj) => {
  // Handle null/undefined inputs
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays - recursively sanitize each element
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item));
  }

  // Handle primitive values
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle objects - remove null/undefined values
  const sanitized = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && value !== undefined) {
      sanitized[key] = sanitizeObject(value);
    }
  });
  return sanitized;
};
